import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink } from 'lucide-react'
import { trackCustomEvent } from '../../utils/metaPixel'

function getStatusKey(status) {
  // Backend may return snake_case statuses (e.g. pending_activation).
  // The translation file contains both snake_case and camelCase keys.
  return status || 'active'
}

const STATUS_CLASSES = {
  active: 'text-green-600',
  cancelled: 'text-red-600',
  finished: 'text-gray-600',
  pending_activation: 'text-brand-orange',
}

const PREFERENCES_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfp1Fahjp79cpUKGz_1HRqZIpyaILPsppJ2qLn1BvTjuCvVMA/viewform'

/**
 * Single subscription row for the Subskrypcje page.
 *
 * - Column 1 (25%): poster.
 * - Column 2 (25%): name, status label, start date, books sent count, actions.
 * - Column 3 (50%): Google Form link for personal subscriptions.
 */
export default function SubscriptionRow({
  sub,
  onCompletePayment,
  onCancel,
}) {
  const { t } = useTranslation()
  const [completing, setCompleting] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const status = getStatusKey(sub.status)
  const isPendingActivation = status === 'pending_activation'
  const isCancellable = status === 'active' || status === 'pending'
  const isPersonal = sub.category === 'personal'

  const handleCompletePayment = async () => {
    if (!onCompletePayment || completing) return
    setCompleting(true)
    try {
      const result = await onCompletePayment(sub.slug)
      if (result?.redirectUrl) {
        trackCustomEvent('CompletePaymentResumed', { content_ids: [sub.slug] })
        window.location.href = result.redirectUrl
      }
    } finally {
      setCompleting(false)
    }
  }

  const handleCancelClick = () => {
    setShowCancelConfirm(true)
  }

  const handleConfirmCancel = async () => {
    if (!onCancel || cancelling) return
    setCancelling(true)
    try {
      await onCancel(sub.slug)
      trackCustomEvent('SubscriptionCancelled', { content_ids: [sub.slug] })
    } finally {
      setCancelling(false)
      setShowCancelConfirm(false)
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 py-6 border-b border-brand-blue/10 last:border-0 items-start">
      {/* Column 1: poster (25%) */}
      <div className="md:col-span-1">
        <img
          src={sub.image}
          alt=""
          className="w-32 h-48 object-cover rounded-xl"
        />
      </div>

      {/* Column 2: info (25%) */}
      <div className="md:col-span-1">
        <h3 className="font-bold text-brand-blue text-lg">{sub.displayName}</h3>
        <p
          className={`font-bold uppercase text-base mt-1 ${
            STATUS_CLASSES[status] || 'text-brand-blue'
          }`}
        >
          {t(`library.mySubscriptions.status.${status}`, status)}
        </p>
        {isPendingActivation ? (
          <p className="text-base text-brand-orange/90 mt-2">
            {t('library.mySubscriptions.pendingActivationHint', 'Oczekuje na płatność. Dokończ ją lub anuluj, aby kupić inną subskrypcję.')}
          </p>
        ) : (
          <p className="text-base text-brand-blue/80 mt-2">
            {t('library.mySubscriptions.from', { date: sub.startDate || '—' })}
          </p>
        )}
        <p className="text-base text-brand-blue/80">
          {t('library.mySubscriptions.booksCount', { count: sub.booksSentCount })}
        </p>
        {(isPendingActivation || isCancellable) && (
          <div className="mt-3 flex flex-col gap-2">
            {isPendingActivation && (
              <button
                type="button"
                onClick={handleCompletePayment}
                disabled={completing}
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-blue rounded-full px-4 py-2 font-bold hover:bg-brand-gold/90 transition text-sm disabled:opacity-60"
              >
                {completing
                  ? t('common.loading')
                  : t('library.mySubscriptions.completePayment', 'Dokończ płatność')}
              </button>
            )}
            {isCancellable && (
              <button
                type="button"
                onClick={handleCancelClick}
                disabled={cancelling}
                className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 font-bold text-sm border border-red-400 text-red-600 hover:bg-red-50 transition disabled:opacity-60"
              >
                {cancelling
                  ? t('common.loading')
                  : t('library.mySubscriptions.cancelSubscription', 'Anuluj subskrypcję')}
              </button>
            )}
          </div>
        )}

        {showCancelConfirm && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-800 font-semibold mb-3">
              {t('library.mySubscriptions.cancelConfirmTitle', 'Anulować subskrypcję?')}
            </p>
            <p className="text-red-700 text-sm mb-4">
              {t('library.mySubscriptions.cancelConfirmBody', 'Subskrypcja zostanie anulowana na koniec obecnego okresu rozliczeniowego. Tej operacji nie można cofnąć.')}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={cancelling}
                className="inline-flex items-center justify-center gap-2 bg-red-600 text-white rounded-full px-4 py-2 font-bold hover:bg-red-700 transition text-sm disabled:opacity-60"
              >
                {cancelling
                  ? t('common.loading')
                  : t('library.mySubscriptions.cancelConfirmYes', 'Tak, anuluj')}
              </button>
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelling}
                className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 font-bold text-sm border border-brand-blue/30 text-brand-blue hover:bg-brand-blue/5 transition disabled:opacity-60"
              >
                {t('library.mySubscriptions.cancelConfirmNo', 'Nie, wróć')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Column 3: Google Form link for personal subscriptions (50%) */}
      {isPersonal && (
        <div className="md:col-span-2">
          <h4 className="font-climate text-lg text-brand-gold mb-3">
            {t('library.mySubscriptions.editPreferences')}
          </h4>
          <a
            href={PREFERENCES_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[var(--library-input-bg)] hover:bg-white/95 rounded-full px-5 py-3 border border-[var(--library-border)] text-brand-blue font-bold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
          >
            <ExternalLink size={18} />
            {t('library.mySubscriptions.preferencesForm', 'Wypełnij formularz preferencji')}
          </a>
        </div>
      )}
    </div>
  )
}
